import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { StaticModal, StaticModalProps } from '../../StaticModal';
import {
  termsAndConditions,
  TermsAndConditionsApi,
} from '@/api/cms/termsAndConditions';
import { termsAndConditionsEE } from '@/api/cms/termsAndConditionsEE';
import { formatDate, getCookie } from '@/helpers/common';
import { Country } from '@/helpers/conf';
import { PAYMENT_VERIFICATION_ORGANIZATION_IP } from '@/helpers/constants';
import { Locale } from '@/helpers/languages';
import { USER_IP } from '@/helpers/variables';
import useEstonia from '@/hooks/estonia';
import { useSwr } from '@/hooks/swr';
import { useCurrentCountry } from '@/hooks/useCurrentCountry';

export const TermsAndConditionsModalContainer: FC<StaticModalProps> = ({
  onClose,
  isPortal,
}) => {
  const {
    i18n: { language },
  } = useTranslation('common');
  const { isEstonia } = useEstonia();

  const isAustralia = useCurrentCountry(Country.Australia);

  const { data, isLoading } = useSwr<TermsAndConditionsApi>({
    key: isEstonia ? 'termsAndConditionsEE' : 'termsAndConditions',
    fetcher: isEstonia ? termsAndConditionsEE : termsAndConditions,
    params: [isAustralia ? Locale.ENGLISH_AUSTRALIA : language],
  });

  return (
    <TermsAndConditionsModal
      data={data}
      isLoading={isLoading}
      onClose={onClose}
      isPortal={isPortal}
    />
  );
};

type TermsAndConditionsModalData = Pick<
  TermsAndConditionsApi,
  'title' | 'text' | 'effectiveFrom' | 'lastUpdated' | 'textAlternative'
>;
interface TermsAndConditionsModalProps extends StaticModalProps {
  data?: TermsAndConditionsModalData;
  isLoading: boolean;
}

export const TermsAndConditionsModal: FC<TermsAndConditionsModalProps> = ({
  onClose,
  isPortal,
  data,
  isLoading,
}) => {
  const { t } = useTranslation('common');

  const isPaymentRegulator =
    getCookie(USER_IP) === PAYMENT_VERIFICATION_ORGANIZATION_IP;

  return (
    <StaticModal
      onClose={onClose}
      pending={isLoading}
      title={data && data.title}
      isPortal={isPortal}
    >
      {data && (
        <>
          <div className="date">
            {t('effectiveFrom')}:{' '}
            {formatDate(data.effectiveFrom, t, 'common:month')}
            <br />
            {t('lastUpdated')}:{' '}
            {formatDate(data.lastUpdated, t, 'common:month')}
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: isPaymentRegulator ? data.textAlternative : data.text,
            }}
          />
        </>
      )}
    </StaticModal>
  );
};
